<template>
  <div>
    <DefaultModal
      :title="modalTitle"
      ref="modal"
      @submit="save"
      :stateless="childModalOpen"
      :hide-overlay="childModalOpen"
    >
      <template v-slot:content>
        <v-form ref="form">
          <v-row>
            <!-- Tenant -->
            <v-col cols="12">
              <v-select
                v-model="formData.tenant_id"
                :items="tenants"
                :label="`${$t('tenant')}`"
                :rules="[v => !!v || $t('field_required')]"
                item-text="title"
                item-value="tenant_id"
                outlined
                dense
              ></v-select>
            </v-col>

            <!-- Title -->
            <v-col cols="12">
              <v-text-field
                v-model="formData.title"
                :label="`${$t('title')}`"
                :rules="[v => !!v || $t('field_required')]"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.description"
                :label="`${$t('description')}`"
                :rules="[v => !!v || $t('field_required')]"
                :rows="2"
                outlined
                dense
              ></v-textarea>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="formData.icon"
                :label="`${$t('icon')}`"
                :rules="[v => !!v || $t('field_required')]"
                :append-icon="formData.icon"
                outlined
                dense
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="formData.form_default"
                :label="`${$t('form_default')}`"
                :rules="[
                  v => !!v || $t('field_required'),
                  v => {
                    try {
                      JSON.parse(v);
                      return true;
                    } catch (e) {
                      return $t('form_rules.invalid_json') || 'Invalid JSON format';
                    }
                  }
                ]"
                :rows="4"
                outlined
                dense
              ></v-textarea>
            </v-col>

            <!-- Pool -->
            <v-col cols="4">
              <v-select
                v-model="formData.owner_pool_id"
                :items="orderedPools"
                :label="`${$t('owner_pool')}`"
                :rules="[v => !!v || $t('field_required')]"
                item-text="pool_title"
                item-value="pool_id"
                outlined
                dense
              ></v-select>
            </v-col>

            <v-col cols="4">
              <v-select
                v-model="formData.pool_id"
                :items="orderedPools"
                :label="`${$t('first_pool')}`"
                :rules="[v => !!v || $t('field_required')]"
                item-text="pool_title"
                item-value="pool_id"
                outlined
                dense
              ></v-select>
            </v-col>

            <!-- Lane -->
            <v-col cols="4">
              <v-select
                v-model="formData.lane_id"
                :items="lanesOptions"
                :label="$t('first_lane')"
                item-text="lane_title"
                item-value="lane_id"
                outlined
                dense
              ></v-select>
            </v-col>

            <!-- Don't Copy Aspect -->
            <v-col cols="12">
              <v-select
                v-model="formData.dont_copy_aspect_id"
                :items="aspectsOptions"
                :label="$t('dont_copy_aspect')"
                item-text="aspect_key"
                item-value="enabler_id"
                outlined
                dense
              ></v-select>
            </v-col>

            <!-- Root Job Folder Structure -->
            <v-col cols="6">
              <v-text-field
                :value="rootJobFolderStructureDisplay"
                :label="$t('root_job_folder_structure')"
                :prepend-inner-icon="rootHasSelection ? 'mdi-close' : ''"
                outlined
                dense
                readonly
                @click:prepend-inner="clearSelections"
                @click="openFolderSelector('root_job_folder_structure')"
                append-icon="mdi-folder-search"
                @click:append="openFolderSelector('root_job_folder_structure')"
              ></v-text-field>
            </v-col>

            <!-- Job Root Destination Node ID -->
            <v-col cols="6">
              <v-text-field
                :value="jobRootDestinationDisplay"
                :label="$t('job_root_destination_node_id')"
                :prepend-inner-icon="destinationHasSelection ? 'mdi-close' : ''"
                outlined
                dense
                readonly
                :disabled="!formData.root_job_folder_structure"
                :rules="destinationRules"
                @click:prepend-inner="clearDestinationSelection"
                @click="openFolderSelector('job_root_destination_node_id')"
                append-icon="mdi-folder-search"
                @click:append="openFolderSelector('job_root_destination_node_id')"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-form>
      </template>
    </DefaultModal>

    <!-- Hidden folder selectors -->
    <SelectFileField
      v-show="false"
      ref="rootJobFolderStructureSelector"
      :label="$t('root_job_folder_structure')"
      :modal-title="$t('root_job_folder_structure')"
      select-current-folder
      :accepts="'folder'"
      :parent-node-id="navigationStartNodeId"
      :root-label="selectedRepositoryName"
      :allow-root-nav="true"
      :persistent="true"
      @selectNodes="onSelectRootJobFolderStructure"
      @opened="onChildModalOpen"
      @closed="onSelectorClosed('root_job_folder_structure')"
      @rootNav="openRepositoryPicker('root_job_folder_structure')"
    />

    <SelectFileField
      v-show="false"
      ref="jobRootDestinationSelector"
      :label="$t('job_root_destination_node_id')"
      :modal-title="$t('job_root_destination_node_id')"
      select-current-folder
      :parent-node-id="navigationStartNodeId"
      :accepts="'folder'"
      :root-label="selectedRepositoryName"
      :allow-root-nav="true"
      :blocked-node-ids="destinationBlockedNodes"
      :blocked-root-id="formData.root_job_folder_structure"
      :persistent="true"
      @selectNodes="onSelectJobRootDestination"
      @opened="onChildModalOpen"
      @closed="onSelectorClosed('job_root_destination_node_id')"
      @rootNav="openRepositoryPicker('job_root_destination_node_id')"
    />

    <v-dialog
      v-model="repositoryDialog"
      max-width="900px"
      style="z-index: 10002;"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title class="d-flex align-center">
          <span>{{ $t('repositories') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="cancelRepositorySelection">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row
            class="fill-height"
            align-content="center"
            justify="center"
            v-if="repositoryLoading"
          >
            <v-col class="text-subtitle-1 text-center" cols="12">
              <span>{{ $t('retrieving_files') }}</span>
            </v-col>
            <v-col cols="6">
              <v-progress-linear
                color="primary"
                indeterminate
                rounded
                height="6"
              ></v-progress-linear>
            </v-col>
          </v-row>

          <v-row v-else-if="repositories.length">
            <v-col
              v-for="repo in repositories"
              :key="repo.repository_id"
              class="d-flex child-flex py-2 px-2"
              xl="3"
              lg="4"
              md="4"
              sm="6"
              cols="12"
            >
              <div style="width: 100%; cursor: pointer;" @click="selectRepository(repo)">
                <RepositoryCard
                  :data="repo"
                  @click-thumb="selectRepository(repo)"
                />
              </div>
            </v-col>
          </v-row>

          <v-row
            v-else
            class="fill-height"
            align-content="center"
            justify="center"
          >
            <v-col class="text-subtitle-1 text-center" cols="12">
              <span>{{ $t('empty') }}</span>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelRepositorySelection">{{ $t('cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>


<script>
import DefaultModal from '@/components/DefaultModal.vue'
import SelectFileField from '@/components/content/default/Form/SelectFileField.vue'
import RepositoryCard from '@/components/content/RepositoryCard.vue'
import JobDefinitionAPI from '@/services/api/job/job_definition'
import ContentAPI from '@/services/api/content'

export default {
  name: "JobDefinitionDialog",

  components: {
    DefaultModal,
    SelectFileField,
    RepositoryCard,
  },

  props: {
    jobDefinition: {
      type: Object,
      default: null
    },
    pools: {
      type: Array,
      required: true
    },
    lanes: {
      type: Array,
      required: true
    },
    aspects: {
      type: Array,
      required: true
    },
    tenants: {
      type: Array,
      required: true
    },
    appTenantId: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      formData: {
        tenant_id: null,
        title: '',
        icon: null,
        description: null,
        form_default: null,
        owner_pool_id: null,
        pool_id: null,
        lane_id: null,
        dont_copy_aspect_id: null,
        root_job_folder_structure: null,
        job_root_destination_node_id: null
      },
      childModalOpen: false,
      selectedRootJobFolderName: '',
      selectedJobRootDestinationName: '',
      repositories: [],
      repositoryDialog: false,
      repositorySelectionTarget: null,
      repositoryLoading: false,
      selectedRepositoryId: null,
      selectedRepositoryNodeId: null,
      selectedRepositoryName: '',
      pendingRepositoryId: null,
      previousRepositoryState: null,
      pendingSelectionForField: null,
      activeSelectorField: null,
      selectionMadeInModal: false,

      destinationRules: [
        v => {
          if (this.formData.root_job_folder_structure && !v) {
            return this.$t('job_root_destination_required')
          }
          return true
        }
      ],
    }
  },

  computed: {
    modalTitle() {
      return this.jobDefinition
        ? this.$t('edit_job_definition')
        : this.$t('new_job_definition')
    },

    rootJobFolderStructureDisplay() {
      return this.selectedRootJobFolderName || this.formData.root_job_folder_structure || ''
    },

    jobRootDestinationDisplay() {
      return this.selectedJobRootDestinationName || this.formData.job_root_destination_node_id || ''
    },

    availableLanesByPool() {
      if (! this.formData.pool_id) {
        return []
      }

      return this.lanes.filter(l => l.pool_id == this.formData.pool_id)
    },

    lanesOptions() {
      return [{
        lane_id: null,
        lane_title: "—"
      }, ...Array.from(this.availableLanesByPool)
          .sort((a, b) => {
            const titleA = a?.lane_title || ''
            const titleB = b?.lane_title || ''
            return titleA.localeCompare(titleB)
          }),
      ]
    },

    aspectsOptions() {
      return [{
        enabler_id: null,
        aspect_key: "—"
      }, ...Array.from(this.aspects)
          .sort((a, b) => {
            const titleA = a?.title || ''
            const titleB = b?.title || ''
            return titleA.localeCompare(titleB)
          }),
      ]
    },

    navigationStartNodeId() {
      return this.selectedRepositoryNodeId || null
    },

    rootHasSelection() {
      return !!this.formData.root_job_folder_structure
    },

    destinationHasSelection() {
      return !!this.formData.job_root_destination_node_id
    },

    destinationBlockedNodes() {
      const blocked = []
      if (this.formData.root_job_folder_structure) {
        blocked.push(this.formData.root_job_folder_structure)
      }
      return blocked
    },

    orderedPools() {
      return [...this.pools].sort((a, b) => {
        const titleA = a?.pool_title || ''
        const titleB = b?.pool_title || ''
        return titleA.localeCompare(titleB)
      })
    },
  },

  watch: {
    jobDefinition: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.selectedRepositoryId = null
          this.selectedRepositoryNodeId = null
          this.selectedRepositoryName = ''
          this.pendingRepositoryId = null

          this.formData = {
            tenant_id: newVal.tenant_id || null,
            title: newVal.title || null,
            icon: newVal.icon || null,
            description: newVal.description || null,
            form_default: newVal.form_default || null,
            owner_pool_id: newVal.owner_pool_id || null,
            pool_id: newVal.pool_id || null,
            lane_id: newVal.lane_id || null,
            root_job_folder_structure: newVal.root_job_folder_structure || null,
            job_root_destination_node_id: newVal.job_root_destination_node_id || null,
            dont_copy_aspect_id: newVal.dont_copy_aspect_id || null,
          }
          this.selectedRootJobFolderName = ''
          this.selectedJobRootDestinationName = ''

          // Fetch node names when loading existing data
          this.fetchNodeNames()
        } else {
          this.formData = {
            tenant_id: this.appTenantId || null,
            title: null,
            icon: null,
            description: null,
            form_default: null,
            owner_pool_id: null,
            pool_id: null,
            lane_id: null,
            root_job_folder_structure: null,
            job_root_destination_node_id: null,
            dont_copy_aspect_id: null,
          }

          this.selectedRootJobFolderName = null
          this.selectedJobRootDestinationName = null
          this.selectedRepositoryId = null
          this.selectedRepositoryNodeId = null
          this.selectedRepositoryName = ''
          this.pendingRepositoryId = null
        }
      }
    },

    '$route.params.businessAppId': {
      immediate: true,
      handler() {
      }
    },

    '$route.params.contentAppId': {
      immediate: true,
      handler() {
        this.loadRepositories()
      }
    }
  },

  methods: {
    openModal() {
      this.$refs.modal.openModal()
    },

    closeModal() {
      this.$refs.modal.closeModal()
    },

    onChildModalOpen() {
      this.childModalOpen = true
    },

    onChildModalClose() {
      this.childModalOpen = false
    },

    openRepositoryPicker(targetField = null) {
      this.repositorySelectionTarget = targetField || this.repositorySelectionTarget

      // If a repository is already locked, skip repo list and just reopen the folder selector
      if (this.selectedRepositoryId && this.selectedRepositoryNodeId) {
        const fieldToOpen = this.repositorySelectionTarget || this.activeSelectorField || 'root_job_folder_structure'
        this.repositoryDialog = false
        this.onChildModalOpen()
        this.activeSelectorField = fieldToOpen
        this.selectionMadeInModal = false
        this.$nextTick(() => this.openSelectorForField(fieldToOpen))
        return
      }

      this.repositoryDialog = true
      this.onChildModalOpen()
      if (!this.repositories.length && !this.repositoryLoading) {
        this.loadRepositories()
      }
    },

    openFolderSelector(fieldName) {
      if (fieldName === 'job_root_destination_node_id' && !this.formData.root_job_folder_structure) {
        return
      }

      this.repositorySelectionTarget = fieldName

      // Snapshot current repo state before a potential change via selection flow
      this.previousRepositoryState = {
        id: this.selectedRepositoryId,
        nodeId: this.selectedRepositoryNodeId,
        name: this.selectedRepositoryName
      }

      if (!this.selectedRepositoryNodeId) {
        this.repositoryDialog = true
        this.onChildModalOpen()
        if (!this.repositories.length && !this.repositoryLoading) {
          this.loadRepositories()
        }
        return
      }

      this.onChildModalOpen()
      this.activeSelectorField = fieldName
      this.selectionMadeInModal = false
      this.openSelectorForField(fieldName)
    },

    openSelectorForField(fieldName) {
      this.$nextTick(() => {
        if (fieldName === 'root_job_folder_structure') {
          const comp = this.$refs.rootJobFolderStructureSelector
          if (comp && typeof comp.selectFile === 'function') {
            comp.selectFile()
          }
        } else if (fieldName === 'job_root_destination_node_id') {
          const comp = this.$refs.jobRootDestinationSelector
          if (comp && typeof comp.selectFile === 'function') {
            comp.selectFile()
          }
        }
      })
    },

    onSelectRootJobFolderStructure(nodes) {
      if (nodes && nodes.length > 0) {
        const node = nodes[0]
        if ((node.node_type || node.type) !== 'folder') {
          return
        }

        const selectedId = node.node_id || node.id || ''
        this.formData.root_job_folder_structure = selectedId
        this.selectedRootJobFolderName = node.name || node.entry?.name || ''

        // Changing structure forces destination to be reselected
        if (this.formData.job_root_destination_node_id) {
          this.formData.job_root_destination_node_id = null
          this.selectedJobRootDestinationName = ''
        }
      }
      this.selectionMadeInModal = true
      this.pendingSelectionForField = null
      this.activeSelectorField = null
      this.previousRepositoryState = null
      this.repositorySelectionTarget = null
      this.onChildModalClose()
    },

    async onSelectJobRootDestination(nodes) {
      if (nodes && nodes.length > 0) {
        const node = nodes[0]
        if ((node.node_type || node.type) !== 'folder') {
          return
        }

        const nodeRepoId = node.repository_id || node.repositoryId || node.repository?.repository_id || node.repository?.id
        if (this.selectedRepositoryId && nodeRepoId && String(nodeRepoId) !== String(this.selectedRepositoryId)) {
          return
        }

        const selectedId = node.node_id || node.id || ''
        const rootId = this.formData.root_job_folder_structure

        if (rootId) {
          // Block selecting the same folder or descendants of the structure
          if (String(selectedId) === String(rootId)) {
            return
          }

          const isDescendant = await this.isDescendantOfRoot(rootId, selectedId)
          if (isDescendant) {
            return
          }
        }

        this.formData.job_root_destination_node_id = selectedId
        this.selectedJobRootDestinationName = node.name || node.entry?.name || ''
      }
      this.selectionMadeInModal = true
      this.pendingSelectionForField = null
      this.activeSelectorField = null
      this.previousRepositoryState = null
      this.repositorySelectionTarget = null
      this.onChildModalClose()
    },

    async fetchNodeNames() {
      // Fetch root_job_folder_structure node name
      if (this.formData.root_job_folder_structure) {
        try {
          const response = await ContentAPI.entry.getEntryData(this.formData.root_job_folder_structure, true)
          if (response.data) {
            this.selectedRootJobFolderName = response.data.name || ''
            this.setRepositoryFromEntry(response.data, 'root')
          }
        } catch (error) {
          console.error('Error fetching root_job_folder_structure node:', error)
        }
      }

      // Fetch job_root_destination_node_id node name
      if (this.formData.job_root_destination_node_id) {
        try {
          const response = await ContentAPI.entry.getEntryData(this.formData.job_root_destination_node_id, true)
          if (response.data) {
            this.selectedJobRootDestinationName = response.data.name || ''
            this.setRepositoryFromEntry(response.data, 'destination')
          }
        } catch (error) {
          console.error('Error fetching job_root_destination_node_id node:', error)
        }
      }
    },

    async loadRepositories() {
      this.repositoryLoading = true
      try {
        const response = await ContentAPI.repository.getAllRepositories({ 'filter[active]': 'true' })
        this.repositories = response.data?.items || []
        if (this.pendingRepositoryId) {
          this.applyRepositoryById(this.pendingRepositoryId)
        }
      } catch (error) {
        console.error('Error loading repositories:', error)
      } finally {
        this.repositoryLoading = false
      }
    },

    applyRepositoryById(repositoryId, { resetFields = false } = {}) {
      if (!repositoryId) return
      const repo = this.repositories.find(r => String(r.repository_id) === String(repositoryId))
      if (repo) {
        this.setSelectedRepository(repo, { resetFields })
        this.pendingRepositoryId = null
      } else {
        this.pendingRepositoryId = repositoryId
      }
    },

    setSelectedRepository(repo, { resetFields = false } = {}) {
      if (!repo) return

      const newRepoId = repo.repository_id || repo.id || null
      const changed = this.selectedRepositoryId && newRepoId && String(this.selectedRepositoryId) !== String(newRepoId)

      this.selectedRepositoryId = newRepoId
      this.selectedRepositoryNodeId = repo.node_id || repo.root_node_id || repo.repository_node_id || this.selectedRepositoryNodeId
      this.selectedRepositoryName = repo.name || this.selectedRepositoryName

      if (changed && resetFields) {
        this.clearFolderSelections()
      }
    },

    onSelectorClosed(fieldName) {
      this.onChildModalClose()

      if (this.selectionMadeInModal) {
        this.selectionMadeInModal = false
        this.activeSelectorField = null
        return
      }

      // If user closed without selecting and this flow was triggered by repo selection, revert repo
      if (this.pendingSelectionForField === fieldName || this.activeSelectorField === fieldName) {
        if (this.previousRepositoryState) {
          this.selectedRepositoryId = this.previousRepositoryState.id
          this.selectedRepositoryNodeId = this.previousRepositoryState.nodeId
          this.selectedRepositoryName = this.previousRepositoryState.name
        } else if (!this.formData.root_job_folder_structure && !this.formData.job_root_destination_node_id) {
          this.clearSelections()
        }

        this.pendingSelectionForField = null
        this.activeSelectorField = null
        this.selectionMadeInModal = false
        this.previousRepositoryState = null
      }
    },

    clearFolderSelections() {
      this.formData.root_job_folder_structure = null
      this.formData.job_root_destination_node_id = null
      this.selectedRootJobFolderName = ''
      this.selectedJobRootDestinationName = ''
    },

    clearDestinationSelection() {
      this.formData.job_root_destination_node_id = null
      this.selectedJobRootDestinationName = ''
    },

    clearSelections() {
      this.clearFolderSelections()
      this.selectedRepositoryId = null
      this.selectedRepositoryNodeId = null
      this.selectedRepositoryName = ''
      this.pendingRepositoryId = null
      this.previousRepositoryState = null
      this.pendingSelectionForField = null
      this.activeSelectorField = null
      this.selectionMadeInModal = false
    },

    selectRepository(repo) {
      const newRepoId = repo?.repository_id || repo?.id
      if (this.selectedRepositoryId && newRepoId && String(newRepoId) !== String(this.selectedRepositoryId)) {
        // Disallow switching repository once one is chosen; just close picker
        this.repositoryDialog = false
        this.repositorySelectionTarget = null
        this.onChildModalClose()
        return
      }

      this.pendingSelectionForField = this.repositorySelectionTarget

      this.setSelectedRepository(repo, { resetFields: true })
      const target = this.repositorySelectionTarget
      this.repositorySelectionTarget = null

      const openTarget = () => {
        if (target) {
          this.activeSelectorField = target
          this.selectionMadeInModal = false
          this.$nextTick(() => this.openSelectorForField(target))
        } else {
          this.onChildModalClose()
        }
      }

      if (this.repositoryDialog) {
        this.repositoryDialog = false
        this.$nextTick(openTarget)
      } else {
        openTarget()
      }
    },

    cancelRepositorySelection() {
      this.repositoryDialog = false
      this.repositorySelectionTarget = null
      this.onChildModalClose()
      this.pendingSelectionForField = null
      this.activeSelectorField = null
      this.selectionMadeInModal = false
      this.previousRepositoryState = null
    },

    setRepositoryFromEntry(entry) {
      const repositoryId = entry?.repository_id || entry?.repositoryId || entry?.repository?.repository_id || entry?.repository?.id
      if (!repositoryId) {
        return
      }

      if (this.selectedRepositoryId && String(this.selectedRepositoryId) !== String(repositoryId)) {
        return
      }

      if (this.repositories.length) {
        this.applyRepositoryById(repositoryId)
      } else if (!this.pendingRepositoryId) {
        this.pendingRepositoryId = repositoryId
      }
    },

    async isDescendantOfRoot(rootId, nodeId) {
      try {
        const response = await ContentAPI.entry.getParentsFromEntry(nodeId)
        const parents = response?.data || []
        return parents.some(parent => String(parent.node_id || parent.id) === String(rootId))
      } catch (e) {
        console.error('Error checking parent chain', e)
        return false
      }
    },

    save() {
      if (!this.$refs.form.validate()) {
        this.$refs.modal.submitting = false

        return
      }

      if (this.formData.root_job_folder_structure && !this.formData.job_root_destination_node_id) {
        this.$refs.modal.errorMessage = this.$t('job_root_destination_required')
        this.$refs.modal.submitting = false
        return
      }

      // Validate tenant_id is present
      if (!this.formData.tenant_id) {
        this.$refs.modal.errorMessage = 'Tenant is required'
        this.$refs.modal.submitting = false
        return
      }

      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      // Use formData instead of fields
      const payload = { ...this.formData }

      let apiCall

      if (this.jobDefinition) {
        const jobDefinitionId = this.jobDefinition.job_definition_id

        apiCall = JobDefinitionAPI.updateJobDefinition(jobDefinitionId, payload)
      } else {
        apiCall = JobDefinitionAPI.createJobDefinition(payload)
      }

      apiCall
        .then(() => {
          const message = this.jobDefinition ? 'job_definition_updated' : 'job_definition_created'
          this.$emit('saved', message)
          this.$refs.modal.closeModal()
        })
        .catch((error) => {
          if (error.response && error.response.data) {
            this.$refs.modal.errorMessage = error.response.data.message || 'An error occurred'
          }
        })
        .finally(() => {
          this.$refs.modal.submitting = false
        })
    }
  }
}
</script>
